import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import { devicesActions } from "../_actions";
import { DeviceUiHelper } from "../_helpers";

import { Device } from "../_models/Device";
import { EverouBleService } from "../_services/EverouBleService";
import { BluetoothService } from "../_services/BluetoothService";
import { CustomModal } from "../_components/CustomModal";
import { CapsuleButton } from "../_components";

export function SingleBleDevice({
    isSendingActionToAnyDevice = false,
    device = Device(),
    globalSetUpFn = () => {},
    globalCleanUpFn = () => {},
} = {}) {

    //LOCAL STATE
    const [isSendingAction, setIsSendingAction] = useState(false);
    const [retries, setRetries] = useState(0);
    const [uiBleError, setUiBleError] = useState(null);
    const [pendingActionCode, setPendingActionCode] = useState(null);

    //LIBRARIES
    const { t } = useTranslation();
    const dispatch = useDispatch();

    //ACTION HANDLERS
    async function onDeviceButtonPress(actionCode, device = Device()) {
        // Show the heads-up only when the browser will actually pop its native
        // chooser, i.e. when it has NOT already granted access to this device.
        // We ask the browser directly (getDevices) instead of trusting our own
        // stored flag, which can drift from the real permission state.
        if (await willShowBrowserChooser(device)) {
            setPendingActionCode(actionCode);
            return;
        }

        runBleAction(actionCode, device);
    }

    async function willShowBrowserChooser(device = Device()) {
        try {
            const permittedDevice = await BluetoothService.getDeviceFromBleCache(
                device.bluetoothId,
            );
            return !permittedDevice;
        } catch (e) {
            // If we can't tell, warn: an extra heads-up beats a surprise chooser.
            console.warn("Could not check BLE permission state", e);
            return true;
        }
    }

    function confirmDeviceSelection() {
        const actionCode = pendingActionCode;
        setPendingActionCode(null);
        runBleAction(actionCode, device);
    }

    function runBleAction(actionCode, device = Device()) {
        EverouBleService.sendBleActionAutoOrManual({
            isBleInProgress: isSendingActionToAnyDevice,
            actionCode,
            device,

            setUp,
            onError,
            cleanUp,
            onSuccess,
            onRetry,
        });

        function setUp() {
            globalSetUpFn();

            setUiBleError(null);
            setRetries(0);
            setIsSendingAction(true);
        }

        function onError(errorMessage) {
            console.error(errorMessage);
            errorMessage && setUiBleError(errorMessage);
            cleanUp();
        }

        function cleanUp() {
            globalCleanUpFn();

            setIsSendingAction(false);
        }

        function onRetry(retries) {
            setRetries(retries);
        }

        async function onSuccess() {
            await dispatch(devicesActions.getDevice(device.uid));
        }
    }

    //VIEW
    return (
        <div className="d-flex justify-content-between px-2">
            <div className="d-flex flex-column position-relative">
                <div className="mt-2 mb-3">
                    {uiBleError && errorMessageUi()}
                    {device.description}
                </div>
            </div>

            <div className="d-flex align-items-center">
                {isSendingAction
                    ? Spinner()
                    : DeviceButton(device)
                }
                {retries !== 0 && retryCounter()}
            </div>

            {chooseDeviceModal()}
        </div>
    );

    function DeviceButton(device) {
        return DeviceUiHelper.getUiButtonForDevice(
            device,
            onDeviceButtonPress,
        );
    }

    function chooseDeviceModal() {
        return (
            <CustomModal
                show={pendingActionCode !== null}
                centered
                title={t("mod_bluetooth_choose_device_title")}
                onHide={() => setPendingActionCode(null)}
                content={
                    <div className="py-2">
                        {t("mod_bluetooth_choose_device_description")}
                    </div>
                }
                footer={
                    <>
                        <CapsuleButton
                            style="DANGER"
                            text={t("global_cancel")}
                            onClick={() => setPendingActionCode(null)}
                        />
                        <CapsuleButton
                            text={t("global_continue")}
                            onClick={confirmDeviceSelection}
                        />
                    </>
                }
            />
        );
    }

    function Spinner () {
        return (
            <div className="my-auto">
                <div className="spinner-border spinner-border-sm" />
            </div>
        );
    }

    function retryCounter() {
        return (
            <div className="ml-1">
                {retries}
            </div>
        );
    }

    function errorMessageUi() {
        return (
            <div className="
                pt-3
                text-danger
                position-absolute
                text-nowrap
            ">
                {t("global_error") + uiBleError}
            </div>
        );
    }
}